import { useKeyboard } from "@react-native-community/hooks";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Text, TextInput } from "react-native-paper";
import Button from "../../../components/Button";
import { useAppTheme } from "../../../useAppTheme";
import { AppIcon } from "../../AppIcon";
import { Avatar } from "../../Avatar";
import Box from "../../Box";
import DialogWrapper from "../../DialogWrapper";
import { sanitizeProfile } from "../../OnboardingApplet/helpers";
import TooltipWrapper from "../../TooltipWrapper";

type Props = {
	visible: boolean;
	onClose: () => void;
	initialUsername: string;
	onSave: (username: string) => void;
	isSaving: boolean;
	error?: string;
};

export const EditUsernameDialog = ({
	visible,
	onClose,
	initialUsername,
	onSave,
	isSaving,
	error,
}: Props) => {
	const theme = useAppTheme();
	const { t } = useTranslation();
	const { keyboardHeight, keyboardShown } = useKeyboard();
	const [draftUsername, setDraftUsername] = useState(initialUsername);

	useEffect(() => {
		if (visible) {
			setDraftUsername(initialUsername);
		}
	}, [visible, initialUsername]);

	const hasChanges = draftUsername !== initialUsername;
	const canSave = draftUsername.length > 0 && hasChanges && !isSaving;

	const handleSave = () => {
		onSave(draftUsername);
	};

	return (
		<DialogWrapper
			visible={visible}
			handleClose={onClose}
			maxWidth="xs"
			bottom={keyboardHeight ? keyboardHeight - 200 : 0}
		>
			<Dialog.Title>{t("account.editUsername.title")}</Dialog.Title>
			<Dialog.Content style={{ gap: 16 }}>
				<Box gap={4}>
					<TextInput
						mode="outlined"
						label={t("onboarding.steps.profile.usernameLabel")}
						value={draftUsername}
						onChangeText={(nextUsername) =>
							setDraftUsername(
								sanitizeProfile({
									username: nextUsername,
									firstName: "",
									lastName: "",
								}).username,
							)
						}
						autoCapitalize="none"
						autoCorrect={false}
						autoComplete="username"
						textContentType="username"
						returnKeyType="done"
						autoFocus
						error={Boolean(error)}
						style={{ backgroundColor: theme.colors.surface }}
					/>
					{error ? (
						<Text
							variant="bodySmall"
							style={{
								color: theme.colors.error,
								paddingHorizontal: 16,
							}}
						>
							{error}
						</Text>
					) : null}
				</Box>
				{draftUsername && !error && !keyboardShown ? (
					<Box gap={6}>
						<TooltipWrapper title={t("onboarding.steps.profile.avatarTooltip")}>
							<Box flexDirection="row" alignItems="center" gap={2}>
								<Text variant="labelMedium">
									{t("onboarding.steps.profile.avatarLabel")}
								</Text>
								<AppIcon
									name="information-outline"
									size={14}
									color={theme.colors.onSurfaceVariant}
								/>
							</Box>
						</TooltipWrapper>
						<Avatar size={72} username={draftUsername} />
					</Box>
				) : null}
			</Dialog.Content>
			<Dialog.Actions>
				<Button onPress={onClose} disabled={isSaving}>
					{t("common.cancel")}
				</Button>
				<Button
					mode="contained"
					onPress={handleSave}
					disabled={!canSave}
					loading={isSaving}
					style={{ minWidth: 100 }}
				>
					{t("account.save")}
				</Button>
			</Dialog.Actions>
		</DialogWrapper>
	);
};
